import { useState, Suspense, useEffect, useRef } from "react";
import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { MdLogout } from "react-icons/md";
import { IoNotificationsOutline } from "react-icons/io5";
import { FiMoon } from "react-icons/fi";
import { BiSun } from "react-icons/bi";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Header, Sidebar } from "gf-ui-components-mui-lib";
import avatar from "@img/avatar.jpg";
import withNotification from "@hoc/withNotification";
//import UserSession from "@utils/sessionTimeout";
import { BiHomeAlt } from "react-icons/bi";
import configuration from "../config/common.json";
import { MainComponent } from "./styles";
import sidebarLinks from "./sidebar.links";
import { NavLink } from "./sidebar.links";
import { modGetAllowedUrls } from "@utils/helpers";
import EVES from "../assets/img/EVES_Ops.png";
import { GroupSVG } from "../assets/svg";
import { ProductListItem, useProductList } from "../hooks/useProductList";
import useLogout from "@hooks/useLogout";

const EVESLogo = () => {
  return (
    <div
      style={{
        width: "125px",
        display: "flex",
        justifyContent: "space-evenly",
        gap: "2px",
        alignItems: "center",
        marginRight: "10px",
      }}
    >
      <img src={EVES} alt="logo" style={{ width: "40px", height: "35px" }} />
      <Typography component="span" sx={{ fontSize: "14px", fontWeight: "600" }}>Eves Ops</Typography>
    </div>
  );
};

const GroupIcon = () => {
  return (
    <div>
      <GroupSVG />
    </div>
  );
};

function MainLayout() {
  const [open, setOpen] = useState(true);
  const [themes, setTheme] = useState<any>("light");
  const [user, setUser] = useState("");
  const navigate = useNavigate();
  const [filteredLinks, setFilteredLinks] = useState<Array<NavLink>>([]);
  // XSM-1536 - SSO Login flow
  // Author: Abhishek Bharti
  const [showProductDropdown, setShowProductDropdown] =
    useState<boolean>(false);
  const { products } = useProductList();
  const { handleLogout } = useLogout();
  // const decodedToken = decodeJwtWithJsrs(token);
  //const productList = decodedToken?.userasset;

  // ref for header to detect outside clicks
  const headerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem("userscope") as string);
    if (permissions) {
      const mod = modGetAllowedUrls(permissions);
      const filterLinks = (links: any) => {
        return links.reduce((filtered: any, link: any) => {
          if (link.isSubLinks) {
            const filteredSublinks = filterLinks(link.sublinks);
            if (filteredSublinks.length > 0) {
              // Include the link only if there are filtered sublinks
              filtered.push({ ...link, sublinks: filteredSublinks });
            }
          } else if (link.id && mod.has(link.id)) {
            // Include the link if it matches the allowedUrls
            filtered.push({ ...link });
          }
          return filtered;
        }, []);
      };

      const defaultHomeLink = {
        id: "$home",
        icon: <BiHomeAlt size={25} />,
        label: "Home",
        url: "/dashboard",
      };

      const updatedLinks = [
        defaultHomeLink,
        ...filterLinks(sidebarLinks._dashboardLinks),
      ];
      setFilteredLinks(updatedLinks);
    }
  }, []);

  const handleCallback = () => {
    setOpen(!open);
  };

  const toggleTheme = () => {
    const body = document.body;
    const theme = body.dataset.theme;
    if (theme === "dark") {
      body.dataset.theme = "light";
      setTheme("light");
    } else {
      body.dataset.theme = "dark";
      setTheme("dark");
    }
  };

  const handleProductSelect = (item: ProductListItem) => {
    window.open(item?.link, "_self");
    setShowProductDropdown(false);
  };

  const changeTheProducts = (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (products && products?.length > 0) {
      setShowProductDropdown(!showProductDropdown);
    } else {
      navigate("/dashboard");
    }
  };

  // Close product dropdown when clicking outside header
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showProductDropdown &&
        headerRef.current &&
        !headerRef.current.contains(event.target as Node)
      ) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProductDropdown]);

  const controls_data = [
    {
      id: "theme",
      label: "Theme",
      icon: themes === "light" ? <FiMoon /> : <BiSun />,
      handleControl: () => toggleTheme(),
    },
    {
      id: "notification",
      label: "Notification",
      icon: <IoNotificationsOutline />,
    },
  ];

  const menuitems = [
    {
      id: "logout",
      label: "Exit",
      icon: <MdLogout />,
      handleItem: () => handleLogout(),
    },
  ];
  const isAuthenticated = () => {
    const accessToken = localStorage.getItem("access_token");
    return !!accessToken;
  };

  useEffect(() => {
    const Localuser = localStorage.getItem("username");
    setUser(Localuser as string);
  }, [user]);

  const theme = useTheme();
  return (
    <>
      {/* <UserSession /> */}
      <Box>
        {isAuthenticated() ? (
          <Box>
            <div ref={headerRef}>
              <Header
                {...{
                  position: "fixed",
                  color: "primary",
                  searchBar: false,
                  controls: controls_data,
                  user: user,
                  menuItems: menuitems,
                  handlecallback: handleCallback,
                  avatarImg: avatar,
                  logo: <EVESLogo />,
                  ...(products &&
                    products.length > 0 && {
                    groupIcon: <GroupIcon />,
                  }),
                  navigate: changeTheProducts,
                  sx: {
                    boxShadow: "none",
                    zIndex: "1200",
                  },
                  isDropdownOpen: showProductDropdown,
                  dropdownItems: products,
                  handleDropdownSelect: handleProductSelect,
                }}
              />
            </div>
            <Box>
              <Sidebar
                {...{
                  Year: new Date().getFullYear().toString(),
                  ProductName: configuration.wso2.ProductName,
                  variant: "permanent",
                  anchor: "left",
                  ROUTE_MAPPER: sidebarLinks?._dashboardRouteMapper,
                  // sidedata: sidebarLinks?._dashboardLinks,
                  sidedata: filteredLinks,
                  openDrawer: open,
                  appbar: false,
                  drawerheader: false,
                  navigate,
                  sx: {
                    "& .MuiPaper-root.MuiDrawer-paper": {
                      borderRight: "0px",
                    },
                    zIndex: "10",
                    fontWeight: 400,
                    fontSize: "14px !important",
                    lineHeight: "24px !important",
                    ".MuiListItemIcon-root": {
                      marginTop: "0px",
                      paddingBottom: !open ? "20px !important" : null,
                    },
                    [theme.breakpoints.down(1441)]: {
                      "& .MuiPaper-root.MuiDrawer-paper": {
                        width: open ? "250px" : "60px",
                        "& .MuiListItemButton-root": {
                          paddingTop: "5px",
                        },
                      },
                    },
                  },
                }}
              />
              <MainComponent theme={theme} {...{ open: open }}>
                <Suspense fallback={<>Loading...</>}>
                  <Outlet />
                </Suspense>
              </MainComponent>
            </Box>
          </Box>
        ) : (
          <Navigate to="/" replace />
        )}
      </Box>
    </>
  );
}

export default withNotification(MainLayout);
